import { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Button, DataTable, FilterChips, OlhoBotao, Pagination, TextField } from "@atlas/ui/web";
import type { StatusNaFolha } from "@atlas/types";
import { POR_PAGINA_PADRAO, recortar } from "../../lib/recorte";
import { downloadCsv } from "../../lib/csv";
import { getBancoPerfil } from "../../lib/banco-propostas";
import { useFiltros } from "../../lib/use-filtros";
import { GRADE_FILTROS } from "../../lib/filtro-cadastros";
import {
  ROTULO_STATUS_NA_FOLHA,
  STATUS_NA_FOLHA,
  linhaCsvBateCarteira,
  ordenarPorStatus,
  statusFolhaDa,
} from "../../lib/bate-carteira";
import { AtualidadeDoRelatorio, CardsPorStatus, ForaDaCarteira, colunasDaCarteira } from "../../components/BateCarteiraPaineis";
import { atlas } from "../../lib/sdk";
import { downloadAuthed } from "../prefeitura/_ui";

/** UI usa YYYY-MM; backend exige YYYYMM. Converte antes de mandar. */
function normalizeCompetencia(ui: string): string {
  return ui.replace(/[^0-9]/g, "").padEnd(6, "0").slice(0, 6);
}
function competenciaAtualUi(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function BancoBateCarteira() {
  const perfil = getBancoPerfil();
  const [competencia, setCompetencia] = useState(competenciaAtualUi());
  const rodar = useMutation({
    mutationFn: (comp: string) => atlas.banco.bateCarteira({ competencia: normalizeCompetencia(comp) }),
  });
  const rodado = rodar.data;
  // Um olho para a tabela inteira: CPF mascarado por padrao, completo ao abrir.
  const [cpfsRevelados, setCpfsRevelados] = useState(false);

  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(POR_PAGINA_PADRAO);
  const [status, setStatus] = useState<StatusNaFolha | null>(null);
  const filtros = useFiltros([{ nome: "status", ligado: status !== null, limpar: () => setStatus(null) }], setPagina);
  const trocarStatus = filtros.filtrar(setStatus);

  /**
   * Uma linha por contrato (#347), na ordem do que exige acao: nao descontado,
   * parcial, suspenso, aguardando retorno, descontado. Conciliacao e' a tela
   * onde so' o que NAO bate interessa (cliente 12/08/2026, item 3).
   */
  const linhas = useMemo(() => {
    if (!rodado) return [];
    const ordenadas = ordenarPorStatus(rodado.linhas);
    return status ? ordenadas.filter((l) => statusFolhaDa(l) === status) : ordenadas;
  }, [rodado, status]);

  // Os arquivos levam o CPF completo: relatorio vai sem censura, no escopo do
  // banco (diretriz de 14/10/2026). A mascara e' so' da tela. O CSV leva o
  // relatorio inteiro, com as mesmas colunas da planilha.
  const exportarCsv = () => {
    if (!rodado) return;
    downloadCsv(`bate-carteira-${rodado.competencia}.csv`, rodado.linhas.map(linhaCsvBateCarteira));
  };
  // Excel sai do servidor: e' la' que mora o gerador de planilha, e o arquivo
  // vem inteiro, com os valores como numero para somar.
  const exportarExcel = () => {
    if (!rodado) return;
    void downloadAuthed(atlas.banco.bateCarteiraPlanilhaUrl(rodado.competencia), `bate-carteira-${rodado.competencia}.xlsx`);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <header>
        <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
          Conciliação
        </span>
        <h1 style={{ margin: "4px 0 0", fontSize: "1.8rem" }}>Bate de carteira mensal</h1>
        <p style={{ color: "var(--text-muted)", margin: "6px 0 0", maxWidth: 680 }}>
          Um contrato por linha: a carteira da consignatária na competência — o contrato ativo nela ou com retorno da folha —
          e o que a folha do órgão fez com cada um. Só o status <strong>Descontado</strong> quer dizer que a parcela saiu inteira.
        </p>
      </header>

      <div style={{ display: "flex", gap: 12, alignItems: "end", flexWrap: "wrap" }}>
        <TextField label="Competência (AAAA-MM)" value={competencia} onChange={(e) => setCompetencia(e.target.value)} />
        <Button onClick={() => { setPagina(1); rodar.mutate(competencia); }} disabled={rodar.isPending}>
          {rodar.isPending ? "Rodando…" : "Rodar conciliação"}
        </Button>
        {rodado && perfil.perms.exportacao ? (
          <>
            <Button
              variant="ghost"
              onClick={exportarExcel}
              disabled={rodado.linhas.length === 0}
              title={rodado.linhas.length === 0 ? "Nada a exportar nesta competência" : "Baixar planilha Excel"}
            >
              Exportar Excel {rodado.linhas.length > 0 ? `(${rodado.linhas.length})` : ""}
            </Button>
            <Button
              variant="ghost"
              onClick={exportarCsv}
              disabled={rodado.linhas.length === 0}
              title={rodado.linhas.length === 0 ? "Nada a exportar nesta competência" : "Baixar CSV"}
            >
              Exportar CSV
            </Button>
          </>
        ) : null}
      </div>

      {rodar.isError ? (
        <div style={{ padding: 12, borderRadius: 10, background: "color-mix(in srgb, var(--danger-500) 12%, transparent)", border: "1px solid var(--danger-500)", fontSize: 13 }}>
          Não foi possível rodar: {(rodar.error as Error).message}
        </div>
      ) : null}

      {rodado ? (
        <>
          <CardsPorStatus resultado={rodado} statusAtivo={status} onStatus={trocarStatus} />
          <AtualidadeDoRelatorio atualidade={rodado.atualidade} />
          <div style={GRADE_FILTROS}>
            <FilterChips
              label="Status"
              value={status ?? ""}
              onChange={(v) => trocarStatus(v ? (v as StatusNaFolha) : null)}
              options={[{ value: "", label: "Todos" }, ...STATUS_NA_FOLHA.map((s) => ({ value: s, label: ROTULO_STATUS_NA_FOLHA[s] }))]}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--text-muted)" }}>
            <OlhoBotao rotulo="CPFs" revelado={cpfsRevelados} onAlternar={() => setCpfsRevelados((v) => !v)} />
            <span>{cpfsRevelados ? "CPFs visíveis" : "CPFs mascarados"}</span>
          </div>
          <DataTable
            columns={colunasDaCarteira(cpfsRevelados)}
            rows={recortar(linhas, pagina, porPagina).itens}
            rowKey={(l) => l.adfBanco}
            emptyState={status ? "Nenhum contrato com este status." : "Nenhum contrato na carteira nesta competência."}
          />
          <Pagination
            total={linhas.length}
            pagina={pagina}
            porPagina={porPagina}
            onPagina={setPagina}
            onPorPagina={setPorPagina}
            rotulo="contratos"
          />
          <ForaDaCarteira itens={rodado.foraDaCarteira} cpfsRevelados={cpfsRevelados} />
        </>
      ) : (
        <div style={{ color: "var(--text-dim)", fontSize: 14 }}>Escolha a competência e rode a conciliação.</div>
      )}
    </div>
  );
}
